import { cn } from "@/lib/utils";
import { AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import PropTypes from "prop-types";

/**
 * Renders an assistant message that represents an error (Claude pattern)
 * Shows error styling with inline retry button
 *
 * This follows the industry standard pattern used by Claude, ChatGPT, Gemini, and Grok
 * where errors appear as assistant message bubbles in the conversation flow.
 *
 * @param {Object} props
 * @param {Object} props.message - The error message object
 * @param {Function} props.onRetry - Callback for retry button
 * @param {boolean} props.isRetrying - Whether a retry is in progress
 * @param {boolean} props.showRetry - Whether to show the retry button (hide for old errors)
 */
export function ErrorAssistantMessage({
  message,
  onRetry,
  isRetrying = false,
  showRetry = true,
}) {
  return (
    <div className="relative flex">
      {/* Error indicator icon */}
      <div className="flex-shrink-0 mr-3 mt-1">
        <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
          <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
        </div>
      </div>

      <div className="flex-1">
        {/* Error message content */}
        <div className="text-sm text-foreground/90">{message.content}</div>

        {/* Inline retry button - only show if this is the most recent error */}
        {showRetry && message.canRetry && onRetry && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onRetry}
            disabled={isRetrying}
            className="mt-2 text-muted-foreground hover:text-foreground"
          >
            <RefreshCw
              className={cn("w-3 h-3 mr-1.5", isRetrying && "animate-spin")}
            />
            {isRetrying ? "Retrying..." : "Retry"}
          </Button>
        )}
      </div>
    </div>
  );
}

ErrorAssistantMessage.propTypes = {
  message: PropTypes.shape({
    id: PropTypes.string,
    content: PropTypes.string.isRequired,
    isError: PropTypes.bool,
    errorType: PropTypes.string,
    canRetry: PropTypes.bool,
  }).isRequired,
  onRetry: PropTypes.func,
  isRetrying: PropTypes.bool,
  showRetry: PropTypes.bool,
};

export default ErrorAssistantMessage;
